import { isOutboundMessage } from 'app/lib/messageDelivery'
import type { MessageJson } from 'app/types/entities'

export const OPT_OUT_LINE = `Reply STOP to opt out.`

/**
 * True when the author already wrote their own opt-out instructions — an
 * opt-out keyword immediately after an instruction verb ("Reply STOP",
 * "text UNSUBSCRIBE to...", "reply with STOP", `reply "STOP"`). Requiring
 * adjacency (rather than same-clause) keeps everyday phrasings like "send
 * the forms by the end of the week" or "reply by end of day to confirm"
 * from matching.
 *
 * The keyword list is CallRail's approved set (STOP, CANCEL, UNSUBSCRIBE,
 * QUIT, END), per their API changelog, 10 Sep 2024. Matching their list
 * matters because the same list decides whether CallRail appends its own
 * opt-out line to a first message.
 *
 * CANCEL is the one keyword that doubles as ordinary appointment vocabulary,
 * so "Reply CANCEL to cancel your appointment" reads as opt-out wording here.
 * That errs toward showing staff the message they typed, which is the safe
 * direction: claiming an append CallRail never made is the worse mistake.
 */
export function hasOptOutWording(text: string): boolean {
  return /\b(reply|text|send)\s+(with\s+(the\s+word\s+)?)?["“']?(STOP|CANCEL|UNSUBSCRIBE|QUIT|END)\b/i.test(text)
}

export type SmsProvider = `twilio` | `callrail` | `unknown`

/** The subset of Location the provider decision needs. */
export type SmsProviderLocation = {
  primaryTrackingNumber?: string | null
  callRailCompanyId?: string | null
  twilioNumbers?: ReadonlyArray<{ phone?: string | null } | null> | null
}

/**
 * Phone identity, last 10 digits. This is the convention already used for
 * thread ids (`smsThreadId.ts`) and opt-out keys. For the US numbers both
 * providers issue it is equivalent to the API's `+1` normalization in
 * `normalizeSmsPhone`.
 */
export function phoneKey(phone: string | null | undefined): string {
  return (phone ?? ``).replace(/\D/g, ``).slice(-10)
}

/**
 * The one rule for "this phone can be matched against OptOut records": the
 * digits-only last 10, and nothing shorter. A partial number would key an
 * opt-out onto the wrong contact, so an unusable phone matches nothing.
 *
 * Shared with the unread counts (`app/lib/unreadOptOuts`) on purpose. Both
 * decide whether a thread is opted out, and if the two rules drift apart the
 * counts and the conversation list start disagreeing again.
 */
export function optOutKeyFor(phone: string | null | undefined): string | undefined {
  const key = phoneKey(phone)
  return key.length === 10 ? key : undefined
}

/**
 * Which provider a location's outbound SMS goes through. Mirrors
 * `resolveSmsProvider` in trackable-api (`src/lib/sendMessageForLocation.ts`):
 * the tracking number being one of the location's Twilio numbers means Twilio,
 * otherwise CallRail if a company is configured. `unknown` means we could not
 * tell, and every caller must treat it as "assume nothing".
 */
export function smsProviderForLocation(location: SmsProviderLocation | null | undefined): SmsProvider {
  const from = phoneKey(location?.primaryTrackingNumber)
  if (!from) return `unknown`
  if (location?.twilioNumbers?.some((number) => phoneKey(number?.phone) === from)) return `twilio`
  return location?.callRailCompanyId ? `callrail` : `unknown`
}

type AppendCandidate = Pick<MessageJson, `id` | `text` | `sentByUserId` | `isAgent` | `channel`>

/**
 * The one message in a conversation that CallRail appended its opt-out line to,
 * inferred. CallRail adds `Reply STOP to opt out.` to the first text of a
 * conversation when the body carries none, and never reports it back to us, so
 * `Message.text` holds our pre-append body (see the API's `sendSms` resolver).
 *
 * `messages` must be in ascending `createdDate` order. Returns undefined when
 * we cannot be reasonably sure, which is the safe direction: under-rendering
 * shows staff the message they typed, over-rendering claims a line the patient
 * may never have received.
 *
 * Known limit: CallRail decides "first" per tracking number and recipient over
 * its own history, which we cannot see. A conversation CallRail had before our
 * thread existed will be inferred wrong. The backend track (storing the
 * provider's delivered text) is what closes that.
 */
export function optOutAppendMessageId(
  messages: ReadonlyArray<AppendCandidate>,
  provider: SmsProvider,
): string | undefined {
  if (provider !== `callrail`) return undefined
  const first = messages.find((message) => message.channel !== `email` && isOutboundMessage(message))
  // Empty text renders as an error bubble, where an appended line would read as
  // nonsense. It also means we never saw what was sent, so we cannot claim it.
  if (!first?.id || !first.text) return undefined
  return hasOptOutWording(first.text) ? undefined : first.id
}

/**
 * The subset of a message the opt-out lookup below reads. `text` is
 * deliberately absent: consent state comes from the `OptOut` record, never from
 * reading what the contact typed, and a field that is not in the type cannot be
 * reached for later.
 */
export type OptOutMessage = Pick<MessageJson, `from` | `to` | `sentByUserId` | `isAgent`>

/**
 * The `createdDate` of the opt-out record covering this message's contact, or
 * undefined when there is none. Truthiness is the whole rule: a contact holding
 * a record is opted out, whatever they have texted since. Both the badge and
 * the list partition route through here, so a hidden thread is always a badged
 * thread; they previously disagreed on short keys.
 */
export function optOutFor(
  message: OptOutMessage | undefined,
  optOuts: ReadonlyMap<string, string>,
): string | undefined {
  if (!message) return undefined
  const contactPhone = isOutboundMessage(message) ? message.to : message.from
  const key = optOutKeyFor(contactPhone)
  return key ? optOuts.get(key) : undefined
}
